import {
	afterNextRender,
	Component,
	computed,
	DestroyRef,
	DOCUMENT,
	ElementRef,
	inject,
	Injector,
	output,
	signal,
	viewChild
} from '@angular/core';
import { UiButtonComponent, UiEmptyStateComponent, UIIconComponent } from '@design-system';
import { UserSettingEventConsumerService } from '../../../../events/user-setting/user-setting-event-consumer.service';
import { Order } from '../../../../models/order.model';
import { OrdersStore } from '../../data-access/orders.store';
import { ToastService } from '../../../../services/toast.service';
import { BottomSheetComponent } from '../../../../components/bottom-sheet/bottom-sheet.component';

/**
 * Standalone delete confirmation for one order or for a batch of them.
 *
 * It owns nothing but the confirmation: callers hand it the order(s) with {@link open} and decide
 * what to restore when {@link dismissed} fires, which is why it works for the card's swipe-delete
 * (nothing to restore), the detail sheet's حذف (the detail sheet comes back) and the list
 * toolbar's bulk delete (the toolbar closes once the batch is away).
 *
 * Both paths go through here so the «تاییدیه سفارش» switch is read in one place — a second
 * delete flow with its own gate is exactly how bulk delete came to ignore the setting.
 */
@Component({
	selector: 'app-order-cancel-bottom-sheet',
	imports: [BottomSheetComponent, UIIconComponent, UiButtonComponent, UiEmptyStateComponent],
	templateUrl: './order-cancel-bottom-sheet.component.html',
	host: {
		'data-cy': 'order-cancel-bottom-sheet'
	}
})
export class OrderCancelBottomSheetComponent {
	private readonly ordersStore = inject(OrdersStore);
	private readonly userSettingConsumer = inject(UserSettingEventConsumerService);
	private readonly injector = inject(Injector);
	private readonly document = inject(DOCUMENT);
	private readonly toastService = inject(ToastService);

	/** `present` resumes after an await, by which point the host may be gone. */
	private isDestroyed = false;

	constructor() {
		inject(DestroyRef).onDestroy(() => (this.isDestroyed = true));
	}

	/**
	 * The orders the confirmation is up for. They arrive as an argument to {@link open} rather
	 * than as a binding: with «تاییدیه سفارش» off the delete is sent inside that same call, and a
	 * caller that picks the orders and raises the sheet in one click handler would be read back
	 * through a binding change detection has not refreshed yet — the previous order, or none.
	 */
	private readonly target = signal<Order[]>([]);

	/** The user backed out — by انصراف, backdrop, swipe-down, Escape or back. */
	readonly dismissed = output<void>();
	/**
	 * The cancel is on its way to the exchange. It fires as the request goes out rather than when
	 * the answer comes back: the sheet closes on the send, so whatever the caller had open behind
	 * it is spent at that same moment — how the exchange answered is said by toast afterwards.
	 */
	readonly cancelled = output<Order[]>();

	private readonly sheet = viewChild<BottomSheetComponent>('sheet');
	private readonly confirmButton = viewChild('confirmButton', { read: ElementRef });

	/** Whatever raised the sheet, so a close hands focus back where it came from. */
	private opener: HTMLElement | null = null;

	/**
	 * The confirm action has been used and its delete is away. It stays set for the rest of this
	 * confirmation's life — the sheet is already on its way down — so the action reads as busy
	 * and a second tap during the closing animation cannot send the same delete twice.
	 * {@link present} clears it for the next confirmation.
	 */
	readonly cancelling = signal(false);

	/** Tells a close that followed the confirm action apart from one the user asked for. */
	private readonly confirmed = signal(false);

	private readonly sending = new Set<number>();

	/**
	 * The order-settings «تأیید قبل از حذف سفارش» switch — its own setting, not the
	 * one that gates submit and edit in the order form. A trader may well want to be
	 * asked before sending an order and not before pulling one, so the handoff gives
	 * each a switch and this sheet answers only to the delete one.
	 */
	readonly confirmationEnabled = computed(() => this.userSettingConsumer.getSetting().order.cancelOrderConfirmation);

	/** A batch names its size, since the sheet shows no order details to count from. */
	readonly confirmTitle = computed(() => {
		const count = this.target().length;
		return count > 1 ? `از حذف ${count} سفارش اطمینان دارید؟` : 'از حذف سفارش اطمینان دارید؟';
	});

	readonly confirmLabel = computed(() => (this.target().length > 1 ? 'حذف سفارش‌ها' : 'حذف سفارش'));

	readonly sheetLabel = computed(() => (this.target().length > 1 ? 'تایید حذف سفارش‌ها' : 'تایید حذف سفارش'));

	/**
	 * Raises the confirmation for `order` — one order or a batch of them — but only when the
	 * "تاییدیه سفارش" switch is on. With it off the delete goes straight to the exchange. Neither
	 * path waits for the answer: it comes back as a toast, so a delete the exchange refuses is
	 * explained with or without a sheet to show it in.
	 */
	async open(order: Order | readonly Order[] | null): Promise<void> {
		this.target.set(order ? (Array.isArray(order) ? [...order] : [order as Order]) : []);

		// Nothing to confirm and nothing to send — but a caller that closed its own sheet to make
		// room for this one still has to hear back, or it is left with nothing on screen.
		if (this.target().length === 0) {
			this.dismissed.emit();
			return;
		}

		if (!this.confirmationEnabled()) {
			// Nothing is raised and nothing will be, so the caller hears back through
			// {@link cancelled}, which `sendCancel` raises before the request leaves.
			this.sendCancel();
			return;
		}

		await this.present();
	}

	close(): Promise<void> {
		return this.sheet()?.close() ?? Promise.resolve();
	}

	/**
	 * Sends the delete and takes the sheet down with it, without waiting to hear back. The
	 * exchange's answer arrives as a toast, which is app-wide and needs no sheet to hold it, so
	 * keeping the confirmation up for the round trip only parks the user in front of a spinner —
	 * and a refusal that lands after the list has moved on is still explained.
	 */
	async onConfirm(): Promise<void> {
		if (this.cancelling()) {
			return;
		}
		this.cancelling.set(true);

		// The user asked for this close, so `onSheetClosed` must not report it as a back-out —
		// the caller has already been told the delete is away.
		this.confirmed.set(true);

		this.sendCancel();
		await this.close();
	}

	onDismiss(): Promise<void> {
		return this.close();
	}

	/** Every way out of a raised sheet lands here, so {@link dismissed} has one home. */
	onSheetClosed(): void {
		// The sheet is off screen on every route through here, so focus goes back to whatever
		// raised it either way.
		this.restoreFocus();

		// A close the confirm action asked for is no back-out: {@link cancelled} went out with
		// the delete, and reporting both would have the caller restore what it just tore down.
		if (this.confirmed()) {
			return;
		}

		this.dismissed.emit();
	}

	private async present(): Promise<void> {
		this.confirmed.set(false);
		this.cancelling.set(false);
		this.opener = this.activeElement();
		await (this.sheet()?.open() ?? Promise.resolve());
		if (this.isDestroyed) {
			return;
		}

		// Move focus onto the confirm action so keyboard and screen reader users land on the
		// newly raised sheet instead of losing focus to <body>.
		this.focusOnceRendered(() => this.confirmButton()?.nativeElement.querySelector('button'));
	}

	/** Sends the cancel and says by toast how the exchange answered, sheet or no sheet. */
	private async sendCancel(): Promise<void> {
		const orders = this.target().filter(order => !this.sending.has(order.orderId));
		if (orders.length === 0) {
			return;
		}

		orders.forEach(order => this.sending.add(order.orderId));

		// The caller hears about the delete as it goes out, not when it lands: by then its sheet
		// is down and the selection it was raised from is spent either way.
		this.cancelled.emit(orders);

		try {
			if (orders.length === 1) {
				await this.ordersStore.cancelOrder(orders[0].orderId);
				// Toasts are app-wide and outlive this screen, so a request that resolves after
				// the user navigated away reports nothing — it would land on wherever they are now.
				if (!this.isDestroyed) {
					this.toastService.info('درخواست حذف سفارش ارسال شد');
				}
				return;
			}

			await this.ordersStore.cancelOrders(orders.map(order => order.orderId));
			if (!this.isDestroyed) {
				this.toastService.info('درخواست حذف گروهی سفارش‌ها ارسال شد');
			}
		} catch (error) {
			const fallback = orders.length > 1 ? 'حذف سفارش‌ها انجام نشد' : 'حذف سفارش با خطا مواجه شد';
			this.refuse(error instanceof Error ? error.message : fallback);
		} finally {
			orders.forEach(order => this.sending.delete(order.orderId));
		}
	}

	/**
	 * Says why the delete did not happen. The confirmation is long gone by the time an answer
	 * lands, so a toast is the only thing left to carry the reason.
	 */
	private refuse(message: string): void {
		// A rejection can land after the user has navigated away, where an app-wide toast would
		// surface it on a screen it has nothing to do with. Same rule the success path follows.
		if (!this.isDestroyed) {
			this.toastService.error(message);
		}
	}

	/**
	 * Hands focus back to whatever raised the sheet. A cancelled order is often gone from the list
	 * by the time this runs, so the opener has to still be in the document to be worth focusing.
	 */
	private restoreFocus(): void {
		const opener = this.opener;
		this.opener = null;
		if (opener?.isConnected) {
			opener.focus();
		}
	}

	/** The target is only focusable once the render that enables it has run. */
	private focusOnceRendered(target: () => HTMLElement | null | undefined): void {
		if (this.isDestroyed) {
			return;
		}
		afterNextRender(() => target()?.focus(), { injector: this.injector });
	}

	private activeElement(): HTMLElement | null {
		const active = this.document.activeElement;
		return active instanceof HTMLElement && active !== this.document.body ? active : null;
	}
}
